import React, { useState, useEffect, useRef } from "react";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

function fmtDate(dt) {
    if (!dt) return null;
    return new Date(dt).toLocaleString("en-AU", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true
    });
}

function fmtEmail(email) {
    if (!email) return null;
    // Shorten email for display: eswar.mullapudi@unitedpetroleum.com.au → eswar.mullapudi@...
    const at = email.indexOf("@");
    if (at > 0 && email.length > 30) return email.substring(0, at) + "@...";
    return email;
}

function fmtName(name, email) {
    if (name) return name;
    if (!email) return null;
    // Extract name from email: eswar.mullapudi@ → Eswar Mullapudi
    const at = email.indexOf("@");
    if (at > 0) {
        return email.substring(0, at).split(/[._]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    }
    return email;
}

function DetailRow({ label, children }) {
    if (!children) return null;
    return (
        <div style={{ display: "flex", gap: 8, fontSize: 13, lineHeight: 1.6 }}>
            <span style={{ color: "var(--text-muted)", fontWeight: 600, minWidth: 90, flexShrink: 0 }}>{label}</span>
            <span style={{ color: "var(--text-secondary)" }}>{children}</span>
        </div>
    );
}

function ageDays(dt) {
    if (!dt) return null;
    const days = Math.floor((Date.now() - new Date(dt).getTime()) / (1000 * 60 * 60 * 24));
    return days;
}

function ageLabel(dt) {
    const days = ageDays(dt);
    if (days === null) return null;
    if (days === 0) return "Today";
    if (days === 1) return "1 day";
    return `${days} days`;
}

function ageColor(dt) {
    const days = ageDays(dt);
    if (days === null) return "var(--text-muted)";
    if (days > 7) return "#ef4444";
    if (days > 3) return "#f97316";
    return "var(--text-muted)";
}

function IssueCard({ issue, onAction }) {
    const { hasPermission } = useAuth();
    const [activeAction, setActiveAction] = useState(null);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [captureProgress, setCaptureProgress] = useState(null);
    const [verification, setVerification] = useState(null);
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const [routeTo, setRouteTo] = useState("");
    const [routingOpts, setRoutingOpts] = useState(null);
    const [recheckOpen, setRecheckOpen] = useState(false);
    const [resending, setResending] = useState(false);
    const [resendResult, setResendResult] = useState(null);
    const [recheckOption, setRecheckOption] = useState("");
    const [recheckCustom, setRecheckCustom] = useState("");
    const [recheckSubmitting, setRecheckSubmitting] = useState(false);
    const pollRef = useRef(null);

    const vStatus = issue.verificationStatus || issue.verification_status;
    useEffect(() => {
        if (vStatus === 'pending' || vStatus === 'in_progress') {
            const poll = async () => {
                try {
                    const result = await api.verificationStatus(issue.id);
                    setVerification(result);
                    if (result.status === 'completed' || result.status === 'failed') {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                    }
                } catch { /* ignore */ }
            };
            poll();
            pollRef.current = setInterval(poll, 10000);
            return () => { if (pollRef.current) clearInterval(pollRef.current); };
        }
        if (vStatus === 'completed' || vStatus === 'failed') {
            setVerification({
                status: vStatus,
                imageUrl: issue.verificationImageUrl || null,
                capturedAt: issue.verificationCapturedAt || issue.verification_captured_at || null,
                error: issue.verificationError || issue.verification_error || null
            });
        }
    }, [issue.id, vStatus]);

    // Fetch routing options when verify action is opened
    useEffect(() => {
        if (activeAction === "verify" && !routingOpts && issue.siteId) {
            api.routingOptions(issue.siteId).then(data => {
                setRoutingOpts(data.teams || []);
            }).catch(() => setRoutingOpts([]));
        }
    }, [activeAction, issue.siteId]);

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);
        try {
            if (activeAction === "action") {
                setCaptureProgress('capturing');
                const result = await api.actionIssue(issue.id, { notes });
                setCaptureProgress(result.verification?.status === 'completed' ? 'done' :
                    result.verification?.status === 'failed' ? 'failed' : 'done');
                if (result.verification?.imageUrl) setVerification(result.verification);
            } else if (activeAction === "verify") {
                await api.verifyIssue(issue.id, { notes, routeTo: routeTo || undefined });
            } else if (activeAction === "reopen") {
                if (!notes.trim()) {
                    setError("A comment is required when reopening");
                    setSubmitting(false);
                    return;
                }
                await api.reopenIssue(issue.id, { comment: notes });
            }
            setActiveAction(null);
            setNotes("");
            setRouteTo("");
            setRoutingOpts(null);
            if (onAction) onAction();
        } catch (err) {
            setError(err.message);
            if (activeAction === "action") setCaptureProgress('failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleScheduleRecheck = async () => {
        setRecheckSubmitting(true);
        setError(null);
        try {
            let recheckAt;
            if (recheckOption === 'custom') {
                recheckAt = new Date(recheckCustom).toISOString();
            } else {
                const hours = parseInt(recheckOption);
                recheckAt = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
            }
            await api.scheduleRecheck(issue.id, { recheckAt });
            setRecheckOpen(false);
            setRecheckOption("");
            setRecheckCustom("");
            if (onAction) onAction();
        } catch (err) {
            setError(err.message);
        } finally {
            setRecheckSubmitting(false);
        }
    };

    const handleResendEmail = async () => {
        setResending(true);
        setResendResult(null);
        try {
            const result = await api.resendEmail(issue.id);
            setResendResult({ ok: true, to: result.sentTo });
            if (onAction) onAction();
        } catch (err) {
            setResendResult({ ok: false, error: err.message });
        } finally {
            setResending(false);
        }
    };

    const handleCancelRecheck = async () => {
        try {
            await api.cancelRecheck(issue.id);
            if (onAction) onAction();
        } catch (err) {
            setError(err.message);
        }
    };

    const effectiveVerification = verification || (vStatus ? {
        status: vStatus,
        imageUrl: issue.verificationImageUrl || null,
        error: issue.verificationError || issue.verification_error || null
    } : null);

    const canResolve = hasPermission('issues.resolve');
    const canVerify = hasPermission('issues.verify');
    const isOpenish = issue.status === 'open' || issue.status === 'reopened';
    const isActioned = issue.status === 'actioned';

    return (
        <div className="issue-card" id={issue.id ? `issue-${issue.id}` : undefined}>
            {/* Header: severity + title + age + status */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span className={`severity ${(issue.severity || "").toLowerCase()}`}>
                    {issue.severity}
                </span>
                <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)", flex: 1 }}>
                    {issue.title}
                </span>
                {issue.firstDetectedAt && issue.status !== 'resolved' && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: ageColor(issue.firstDetectedAt), whiteSpace: "nowrap" }}>
                        {ageLabel(issue.firstDetectedAt)} old
                    </span>
                )}
                {issue.status && issue.status !== "open" && (
                    <span className={`issue-status issue-status-${issue.status}`}>
                        {issue.status}
                    </span>
                )}
                {issue.reopenCount > 0 && (
                    <span className="issue-status issue-status-reopened" title={issue.reopenComment || ''}>
                        reopened {issue.reopenCount}x
                    </span>
                )}
                {issue.source === 'routed' && issue.parentIssueId && (
                    <span className="issue-status" style={{ background: "rgba(99,102,241,0.12)", color: "#6366f1", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4 }}>
                        Routed from #{issue.parentIssueId}
                    </span>
                )}
            </div>

            {/* Main content: image + details */}
            <div style={{ display: "flex", gap: 14 }}>
                {issue.imageUrl && (
                    <img
                        src={issue.imageUrl}
                        alt={issue.title}
                        style={{
                            width: 140, height: 95, objectFit: "cover", borderRadius: 6,
                            flexShrink: 0, cursor: "pointer",
                            border: "1px solid var(--border)"
                        }}
                        onClick={() => setLightboxUrl(issue.imageUrl)}
                    />
                )}
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <DetailRow label="Fixture">{issue.zone && issue.fixtureType ? `${issue.zone} ${issue.fixtureType}` : issue.fixtureType || issue.zone || null}</DetailRow>
                    {issue.description && (
                        <DetailRow label="Issue">{issue.description}</DetailRow>
                    )}
                    <DetailRow label="Store">{issue.storeName ? `${issue.storeName} (#${issue.siteId})` : issue.siteId ? `#${issue.siteId}` : null}</DetailRow>
                    <DetailRow label="Site CA">
                        {issue.siteContactName ? (
                            <span>
                                {issue.siteContactName}
                                {issue.siteContactNumber && <span style={{ color: "var(--text-muted)" }}> ({issue.siteContactNumber})</span>}
                            </span>
                        ) : null}
                    </DetailRow>
                    <DetailRow label="Raised by">{fmtName(issue.createdByName, issue.assignedBy)}</DetailRow>
                    <DetailRow label="Raised">{fmtDate(issue.firstDetectedAt)}</DetailRow>
                    <DetailRow label="Assigned to">
                        {issue.assignedToTeam && (
                            <span>
                                {issue.assignedToTeam}
                                {issue.assignedToEmail && <span style={{ color: "var(--text-muted)" }}> ({fmtEmail(issue.assignedToEmail)})</span>}
                            </span>
                        )}
                    </DetailRow>
                    {issue.emailSent ? (
                        <DetailRow label="Email sent">
                            {issue.emailSentTo || issue.assignedToEmail || "Yes"}
                            {issue.emailCc && <span style={{ color: "var(--text-muted)" }}> CC: {issue.emailCc}</span>}
                            {issue.emailSentAt && <span style={{ color: "var(--text-muted)" }}> — {fmtDate(issue.emailSentAt)}</span>}
                            {canResolve && issue.assignedToEmail && (
                                <button
                                    onClick={handleResendEmail}
                                    disabled={resending}
                                    style={{
                                        marginLeft: 8, fontSize: 11, padding: "1px 8px", borderRadius: 4,
                                        border: "1px solid var(--border)", background: "var(--bg-hover)",
                                        color: "var(--text-secondary)", cursor: resending ? "wait" : "pointer",
                                    }}
                                >
                                    {resending ? "Sending..." : "Resend"}
                                </button>
                            )}
                            {resendResult && (
                                <span style={{ marginLeft: 6, fontSize: 11, color: resendResult.ok ? "#22c55e" : "#ef4444" }}>
                                    {resendResult.ok ? `Sent to ${resendResult.to}` : resendResult.error}
                                </span>
                            )}
                        </DetailRow>
                    ) : issue.assignedToEmail && canResolve ? (
                        <DetailRow label="Email">
                            <span style={{ color: "var(--text-muted)" }}>Not sent</span>
                            <button
                                onClick={handleResendEmail}
                                disabled={resending}
                                style={{
                                    marginLeft: 8, fontSize: 11, padding: "1px 8px", borderRadius: 4,
                                    border: "1px solid var(--primary)", background: "rgba(59,130,246,0.1)",
                                    color: "var(--primary)", cursor: resending ? "wait" : "pointer",
                                }}
                            >
                                {resending ? "Sending..." : "Send Email"}
                            </button>
                            {resendResult && (
                                <span style={{ marginLeft: 6, fontSize: 11, color: resendResult.ok ? "#22c55e" : "#ef4444" }}>
                                    {resendResult.ok ? `Sent to ${resendResult.to}` : resendResult.error}
                                </span>
                            )}
                        </DetailRow>
                    ) : null}
                    {issue.smsSent ? (
                        <DetailRow label="SMS sent">
                            {issue.assignedToEmail || "Yes"}
                            {issue.smsSentAt && <span style={{ color: "var(--text-muted)" }}> — {fmtDate(issue.smsSentAt)}</span>}
                        </DetailRow>
                    ) : issue.smsRequested ? (
                        <DetailRow label="SMS">
                            <span style={{ color: "var(--yellow, #f9a825)" }}>Requested (pending)</span>
                        </DetailRow>
                    ) : null}
                    {issue.actionedBy && (
                        <DetailRow label="Actioned">
                            {fmtName(null, issue.actionedBy)}
                            {issue.actionedAt && <span style={{ color: "var(--text-muted)" }}> — {fmtDate(issue.actionedAt)}</span>}
                            {issue.resolutionNotes && (
                                <div style={{ fontStyle: "italic", color: "var(--text-secondary)", marginTop: 2, fontSize: 12 }}>"{issue.resolutionNotes}"</div>
                            )}
                        </DetailRow>
                    )}
                    {!issue.actionedBy && issue.resolutionNotes && (
                        <DetailRow label="Notes">{issue.resolutionNotes}</DetailRow>
                    )}
                    {issue.resolvedAt && (
                        <DetailRow label="Resolved">{fmtDate(issue.resolvedAt)}</DetailRow>
                    )}
                    {issue.verifiedBy && (
                        <DetailRow label="Verified">
                            {fmtName(null, issue.verifiedBy)}
                            {issue.verifiedAt && <span style={{ color: "var(--text-muted)" }}> — {fmtDate(issue.verifiedAt)}</span>}
                        </DetailRow>
                    )}
                    {issue.source === "human" && issue.reviewerComment && (
                        <DetailRow label="Comment">
                            <span style={{ fontStyle: "italic" }}>"{issue.reviewerComment}"</span>
                        </DetailRow>
                    )}
                </div>
            </div>

            {/* Verification section */}
            {effectiveVerification && (
                <div style={{
                    marginTop: 10, padding: "8px 10px",
                    background: "var(--bg-secondary)", borderRadius: 6, fontSize: 13
                }}>
                    {(effectiveVerification.status === 'pending' || effectiveVerification.status === 'in_progress') && (
                        <div style={{ color: "var(--text-secondary)", fontStyle: "italic", display: "flex", alignItems: "center", gap: 8 }}>
                            <span className="spinner" style={{ width: 14, height: 14 }} />
                            Capturing verification photo...
                        </div>
                    )}
                    {effectiveVerification.status === 'completed' && effectiveVerification.imageUrl && (
                        <div>
                            <div style={{ fontWeight: 500, marginBottom: 6 }}>Before / After</div>
                            <div style={{ display: "flex", gap: 10 }}>
                                {issue.imageUrl && (
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 2 }}>Before</div>
                                        <img src={issue.imageUrl} alt="Before" style={{ width: 180, height: 120, objectFit: "cover", borderRadius: 4, cursor: "pointer", border: "1px solid var(--border)" }} onClick={() => setLightboxUrl(issue.imageUrl)} />
                                    </div>
                                )}
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 2 }}>After</div>
                                    <img src={effectiveVerification.imageUrl} alt="After" style={{ width: 180, height: 120, objectFit: "cover", borderRadius: 4, cursor: "pointer", border: "2px solid var(--green)" }} onClick={() => setLightboxUrl(effectiveVerification.imageUrl)} />
                                </div>
                            </div>
                        </div>
                    )}
                    {effectiveVerification.status === 'failed' && (
                        <div style={{ color: "var(--red)" }}>
                            Verification capture failed: {effectiveVerification.error || 'Unknown error'}
                        </div>
                    )}
                </div>
            )}

            {issue.recheckDueAt && (
                <div style={{
                    fontSize: 12, fontWeight: 500, color: "#8b5cf6",
                    padding: "6px 12px", background: "rgba(139,92,246,0.08)",
                    borderRadius: 6, margin: "4px 0",
                    display: "flex", alignItems: "center", justifyContent: "space-between"
                }}>
                    <span>
                        Recheck scheduled: {fmtDate(issue.recheckDueAt)}
                        {issue.recheckRequestedBy && (
                            <span style={{ color: "var(--text-muted)" }}>
                                {" "}by {fmtName(null, issue.recheckRequestedBy)}
                            </span>
                        )}
                    </span>
                    {canResolve && (
                        <button
                            onClick={handleCancelRecheck}
                            style={{
                                background: "none", border: "none", color: "#ef4444",
                                cursor: "pointer", fontSize: 11, fontWeight: 600, textDecoration: "underline"
                            }}
                        >
                            Cancel
                        </button>
                    )}
                </div>
            )}

            {captureProgress === 'capturing' && (
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--accent)", padding: "6px 12px", display: "flex", alignItems: "center", gap: 8, margin: "4px 0" }}>
                    <span className="spinner" style={{ width: 14, height: 14 }} />
                    Capturing verification image...
                </div>
            )}

            {/* Reopened banner */}
            {issue.status === 'reopened' && issue.reopenComment && (
                <div style={{
                    fontSize: 12, color: "#ef4444", fontWeight: 500,
                    padding: "6px 12px", background: "rgba(239,68,68,0.06)", borderRadius: 6, margin: "8px 0",
                    borderLeft: "3px solid #ef4444"
                }}>
                    <strong>Reopened:</strong> {issue.reopenComment}
                    {issue.reopenedBy && <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>— {fmtName(null, issue.reopenedBy)}</span>}
                </div>
            )}

            {/* Action buttons */}
            <div className="issue-actions" style={{ marginTop: 8 }}>
                {isOpenish && canResolve && (
                    <button
                        className={`issue-action-btn ${activeAction === "action" ? "active" : ""}`}
                        onClick={() => setActiveAction(activeAction === "action" ? null : "action")}
                    >
                        Actioned
                    </button>
                )}
                {isActioned && canVerify && (
                    <button
                        className={`issue-action-btn ${activeAction === "verify" ? "active" : ""}`}
                        onClick={() => setActiveAction(activeAction === "verify" ? null : "verify")}
                        style={{ borderColor: "#22c55e", color: activeAction === "verify" ? "#fff" : "#22c55e", background: activeAction === "verify" ? "#22c55e" : "transparent" }}
                    >
                        Verify
                    </button>
                )}
                {isActioned && canResolve && (
                    <button
                        className={`issue-action-btn ${activeAction === "reopen" ? "active" : ""}`}
                        onClick={() => setActiveAction(activeAction === "reopen" ? null : "reopen")}
                        style={{ borderColor: "#ef4444", color: activeAction === "reopen" ? "#fff" : "#ef4444", background: activeAction === "reopen" ? "#ef4444" : "transparent" }}
                    >
                        Reopen
                    </button>
                )}
                {isActioned && canResolve && !issue.recheckDueAt && (
                    <button
                        className={`issue-action-btn ${recheckOpen ? "active" : ""}`}
                        onClick={() => { setRecheckOpen(!recheckOpen); setActiveAction(null); }}
                        style={{ borderColor: "#8b5cf6", color: recheckOpen ? "#fff" : "#8b5cf6", background: recheckOpen ? "#8b5cf6" : "transparent" }}
                    >
                        Schedule Recheck
                    </button>
                )}
            </div>

            {isActioned && activeAction !== "verify" && activeAction !== "reopen" && (
                <div style={{
                    fontSize: 12, fontWeight: 600, color: "#f97316",
                    padding: "4px 12px", background: "rgba(249,115,22,0.08)", borderRadius: 6, margin: "4px 0"
                }}>
                    Actioned — awaiting verification
                </div>
            )}

            {activeAction && (
                <div className="issue-action-form">
                    <textarea
                        className="action-input"
                        placeholder={activeAction === "reopen" ? "Why is this being reopened? (required)" : "Notes (optional)..."}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                    />
                    {activeAction === "verify" && routingOpts && (
                        <div style={{ marginTop: 6 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>
                                Route to next team (optional)
                            </label>
                            <select
                                value={routeTo}
                                onChange={(e) => setRouteTo(e.target.value)}
                                style={{
                                    width: "100%", padding: "6px 8px", fontSize: 13,
                                    borderRadius: 6, border: "1px solid var(--border)",
                                    background: "var(--bg-primary)", color: "var(--text-primary)"
                                }}
                            >
                                <option value="">— None —</option>
                                {routingOpts.map(t => (
                                    <option key={t.team} value={t.team} disabled={!t.email}>
                                        {t.team}{t.email ? '' : ' (not configured)'}
                                    </option>
                                ))}
                            </select>
                            {routeTo && routingOpts.find(t => t.team === routeTo)?.email && (
                                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                                    Email: {routingOpts.find(t => t.team === routeTo).email}
                                </div>
                            )}
                        </div>
                    )}
                    {error && <div className="action-error">{error}</div>}
                    <div className="action-buttons">
                        <button className="action-submit" onClick={handleSubmit} disabled={submitting}>
                            {submitting ? "Submitting..." : activeAction === "action" ? "Submit Actioned" : activeAction === "reopen" ? "Submit Reopen" : routeTo ? "Verify & Route" : "Submit Verify"}
                        </button>
                        <button className="action-cancel" onClick={() => { setActiveAction(null); setError(null); setCaptureProgress(null); setRouteTo(""); setRoutingOpts(null); }}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {recheckOpen && (
                <div className="issue-action-form">
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>
                        Recheck in:
                    </label>
                    <select
                        value={recheckOption}
                        onChange={(e) => setRecheckOption(e.target.value)}
                        style={{
                            width: "100%", padding: "6px 8px", fontSize: 13,
                            borderRadius: 6, border: "1px solid var(--border)",
                            background: "var(--bg-primary)", color: "var(--text-primary)"
                        }}
                    >
                        <option value="">Select timing...</option>
                        <option value="1">1 hour</option>
                        <option value="2">2 hours</option>
                        <option value="6">6 hours</option>
                        <option value="12">12 hours</option>
                        <option value="24">24 hours</option>
                        <option value="48">48 hours</option>
                        <option value="168">1 week</option>
                        <option value="336">2 weeks</option>
                        <option value="720">1 month</option>
                        <option value="custom">Custom date/time...</option>
                    </select>
                    {recheckOption === 'custom' && (
                        <input
                            type="datetime-local"
                            value={recheckCustom}
                            onChange={(e) => setRecheckCustom(e.target.value)}
                            style={{
                                width: "100%", padding: "6px 8px", fontSize: 13, marginTop: 6,
                                borderRadius: 6, border: "1px solid var(--border)",
                                background: "var(--bg-primary)", color: "var(--text-primary)"
                            }}
                        />
                    )}
                    {error && <div className="action-error">{error}</div>}
                    <div className="action-buttons">
                        <button
                            className="action-submit"
                            onClick={handleScheduleRecheck}
                            disabled={recheckSubmitting || !recheckOption || (recheckOption === 'custom' && !recheckCustom)}
                            style={{ background: "#8b5cf6" }}
                        >
                            {recheckSubmitting ? "Scheduling..." : "Schedule Recheck"}
                        </button>
                        <button className="action-cancel" onClick={() => { setRecheckOpen(false); setError(null); }}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {lightboxUrl && (
                <div className="lightbox-overlay" onClick={() => setLightboxUrl(null)}>
                    <img src={lightboxUrl} alt="Full size" className="lightbox-image" onClick={(e) => e.stopPropagation()} />
                    <button className="lightbox-close" onClick={() => setLightboxUrl(null)}>&times;</button>
                </div>
            )}
        </div>
    );
}

export default IssueCard;
